// Inline "+ Create new role" mini-form (PRODOPSAI-521).
//
// Rendered inside the same <form> as the parent picker — NOT a nested
// <dialog> (S6819) and NOT a new modal. Used by AddMappingDialog and
// EditMembershipPopover to add a role on the fly without losing the
// parent flow.
//
// Helpers (deriveTeamRoleKey, isLikelyDuplicateRoleName, isConflictError,
// isValidationError) live in `./add-mapping-helpers.ts` so SonarCloud rules
// S7721 + S3776 cannot fire on this file.

import { useCallback, useMemo, useState } from 'react';
import type { ITeamRole } from '@skystone/shared';
import { Button } from '@/components/ui';
import { inputClass } from '@/lib/form-styles';
import { getErrorMessage } from '@/lib/utils';
import { teamMembershipApi } from '@/services/team-membership-api';
import {
  deriveTeamRoleKey,
  isConflictError,
  isLikelyDuplicateRoleName,
  isValidationError,
} from './add-mapping-helpers';

interface InlineCreateRoleFormProps {
  /** Active role catalog used for the soft-collision warning. */
  existingRoles: readonly ITeamRole[];
  /** Cancel (back to picker) — fired on Cancel button or after Use existing. */
  onCancel: () => void;
  /**
   * Fired when the backend has confirmed creation OR the user chose the
   * existing duplicate. The parent should refetch roles and select `role.id`.
   */
  onCreated: (role: ITeamRole) => void;
}

export function InlineCreateRoleForm({
  existingRoles,
  onCancel,
  onCreated,
}: Readonly<InlineCreateRoleFormProps>) {
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const derivedKey = useMemo(() => deriveTeamRoleKey(displayName), [displayName]);
  const duplicate = useMemo(
    () => isLikelyDuplicateRoleName(displayName, existingRoles),
    [displayName, existingRoles],
  );

  const trimmedName = displayName.trim();
  const canSubmit =
    trimmedName !== '' && derivedKey !== '' && submitting === false;

  const handleSubmit = useCallback(async () => {
    if (canSubmit === false) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await teamMembershipApi.createTeamRole({
        key: derivedKey,
        displayName: trimmedName,
      });
      onCreated(created);
    } catch (err: unknown) {
      if (isConflictError(err) === true) {
        setError('A role with that key already exists. Try a different name.');
      } else if (isValidationError(err) === true) {
        setError(getErrorMessage(err, 'Validation error'));
      } else {
        setError('Failed to create role');
      }
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, derivedKey, trimmedName, onCreated]);

  const handleUseExisting = useCallback(() => {
    if (duplicate !== null) {
      onCreated(duplicate);
    }
  }, [duplicate, onCreated]);

  return (
    <div
      className="space-y-2 rounded-md border border-border bg-muted/30 p-3"
      data-testid="inline-create-role-form"
    >
      <label htmlFor="inline-create-role-name" className="block text-xs font-medium">
        Display name
      </label>
      <input
        id="inline-create-role-name"
        type="text"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        onKeyDown={(e) => {
          // Rabizard #665 finding 1: this <input> lives inside the parent
          // dialog's <form>. Without intercepting Enter the keystroke bubbles
          // to the parent and submits the entire Add Mapping dialog with
          // incomplete state. Capture Enter and run the inline create flow
          // instead.
          if (e.key === 'Enter') {
            e.preventDefault();
            if (canSubmit === true) {
              handleSubmit();
            }
          }
        }}
        maxLength={100}
        placeholder="e.g. Senior Test Lead"
        className={inputClass}
        data-testid="inline-create-role-name"
      />
      {derivedKey !== '' && (
        <p
          className="text-xs text-muted-foreground"
          data-testid="inline-create-role-key-preview"
        >
          {`(key auto-derived: ${derivedKey})`}
        </p>
      )}
      {duplicate !== null && (
        <div
          className="rounded-md bg-warning-50 px-2 py-1 text-xs text-warning-700"
          data-testid="inline-create-role-duplicate-hint"
        >
          {`A role named `}
          <strong>{duplicate.displayName}</strong>
          {` already exists. `}
          <button
            type="button"
            onClick={handleUseExisting}
            className="font-semibold text-primary underline"
            data-testid="inline-create-role-use-existing"
          >
            Use existing
          </button>
        </div>
      )}
      {error !== null && (
        <p
          className="text-xs text-error-600"
          role="alert"
          data-testid="inline-create-role-error"
        >
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={submitting}
          data-testid="inline-create-role-cancel"
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => { handleSubmit(); }}
          isLoading={submitting}
          disabled={canSubmit === false}
          data-testid="inline-create-role-submit"
        >
          Create role
        </Button>
      </div>
    </div>
  );
}
